// The decisions behind the visual tour (js/ui/tour.js) that need no DOM: whether
// to offer it to the account that just signed in, which tour that is, and where
// its card goes on the screen. Pure, so they run under node:test.

/**
 * userProfile.tour. 'pending' is written once, when the account's user document
 * is created on its first confirmed sign-in (ensureUserDoc in
 * js/services/cloud-sync.js) — which is what makes an account NEW here. Accounts
 * from before the tour have no value at all, and are never asked.
 */
export const TOUR_PENDING = 'pending';
export const TOUR_TAKEN = 'taken';
export const TOUR_DECLINED = 'declined';

/**
 * Whether to offer the tour now, and which one.
 *
 * Two sources of the role, for two different questions. The PROFILE says whether
 * this account's own data has arrived: a new account's profile has no role until
 * the sign-in has loaded it, together with the 'pending' mark. The TOKEN says
 * what the account is now: coming back from paying for an arbiter account, the
 * profile kept on this device still says player for a moment after the token
 * already says arbiter.
 *
 * @param {object} o
 * @param {object|null} o.profile  state.userProfile: `role` and `tour`
 * @param {string} [o.claimRole]  the role in the account's token ('' when it has none)
 * @param {object|null|undefined} o.billing  the account's billingApi status —
 *        undefined while it is still being asked for, null when there is none
 * @param {boolean} [o.billingWaitOver]  stop waiting for that answer
 * @returns {'organizer'|'player'|'wait'|null} the tour to offer, 'wait' to ask
 *          again shortly, or null for nothing to offer
 */
export function tourOfferFor({ profile, claimRole = '', billing, billingWaitOver = false }) {
    // The account's own profile, with its role, arrives a moment after sign-in.
    if (!profile || !profile.role) return 'wait';
    if (profile.tour !== TOUR_PENDING) return null;
    if ((claimRole || profile.role) !== 'player') return 'organizer';

    // A player account that chose a plan when it signed up is an arbiter on its
    // way: "Complete your subscription" comes first, and its tour is the arbiter
    // one, offered on the sign-in after the subscription has made it one.
    if (billing === undefined && !billingWaitOver) return 'wait';
    if (billing && billing.pendingPlan) return null;
    return 'player';
}

/**
 * The smallest rectangle around every rectangle given, grown by `pad` on each
 * side. Empty rectangles (an element with no box) are left out; null when none
 * is left.
 */
export function unionRect(rects, pad = 0) {
    const list = (rects || []).filter(r => r && r.width > 0 && r.height > 0);
    if (list.length === 0) return null;
    const left = Math.min(...list.map(r => r.left)) - pad;
    const top = Math.min(...list.map(r => r.top)) - pad;
    const right = Math.max(...list.map(r => r.right)) + pad;
    const bottom = Math.max(...list.map(r => r.bottom)) + pad;
    return { left, top, right, bottom, width: right - left, height: bottom - top };
}

/**
 * Where the card goes for one step.
 *
 * Beside the lit-up area when there is room — right first, because most steps
 * point at the sidebar on the left — then below, above or to the left. On a
 * phone there is no room beside anything, so the card takes the bottom edge of
 * the screen, or the top edge when the lit-up area is in the lower part. A step
 * with nothing to point at, or with no room anywhere, puts the card in the
 * middle.
 *
 * @param {object} o
 * @param {{left:number,top:number,right:number,bottom:number}|null} o.target
 * @param {{width:number,height:number}} o.card
 * @param {{width:number,height:number}} o.viewport
 * @param {number} [o.margin]  the distance kept from the screen's edges
 * @param {number} [o.gap]  the distance between the lit-up area and the card
 * @param {number} [o.phoneWidth]  at or below this width the card docks to an edge
 * @returns {{mode: string, left: number, top: number}}
 */
export function placeTourCard({ target, card, viewport, margin = 12, gap = 14, phoneWidth = 640 }) {
    const vw = viewport.width;
    const vh = viewport.height;
    const clampX = x => Math.max(margin, Math.min(x, vw - card.width - margin));
    const clampY = y => Math.max(margin, Math.min(y, vh - card.height - margin));
    const middle = { left: clampX((vw - card.width) / 2), top: clampY((vh - card.height) / 2) };

    if (!target) return { mode: 'center', ...middle };

    if (vw <= phoneWidth) {
        const targetMiddle = (target.top + target.bottom) / 2;
        return targetMiddle > vh * 0.55
            ? { mode: 'dock-top', left: margin, top: margin }
            : { mode: 'dock-bottom', left: margin, top: Math.max(margin, vh - card.height - margin) };
    }

    const besideY = clampY((target.top + target.bottom) / 2 - card.height / 2);
    const besideX = clampX((target.left + target.right) / 2 - card.width / 2);

    if (vw - target.right >= card.width + gap + margin) {
        return { mode: 'right', left: target.right + gap, top: besideY };
    }
    if (vh - target.bottom >= card.height + gap + margin) {
        return { mode: 'below', left: besideX, top: target.bottom + gap };
    }
    if (target.top >= card.height + gap + margin) {
        return { mode: 'above', left: besideX, top: target.top - gap - card.height };
    }
    if (target.left >= card.width + gap + margin) {
        return { mode: 'left', left: target.left - gap - card.width, top: besideY };
    }
    return { mode: 'over', ...middle };
}
